import { type FC, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  BarPlot,
  ChartsAxis,
  ChartsAxisHighlight,
  ChartsDataProvider,
  ChartsGrid,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsWrapper,
  LinePlot,
  MarkPlot,
  type BarSeriesType,
  type LineSeriesType,
} from "@mui/x-charts";
import type { DashboardChartModel } from "../model";

interface ComposedDashboardChartProps {
  model: DashboardChartModel;
  height: number;
  axisLeft: (value: number) => string;
  /** Есть только у графика с правой осью. */
  axisRight?: (value: number) => string;
  tooltip: (
    value: number | null,
    dataIndex: number,
    seriesId: string
  ) => string;
}

/**
 * Столбцы и линии в одном графике через композицию MUI X Charts: ось X
 * по шагам периода (band), левая ось и, если нужно, правая. Цвета серий
 * из палитры темы, легенда переключает серии по клику, подсказка по оси.
 */
export const ComposedDashboardChart: FC<ComposedDashboardChartProps> = ({
  model,
  height,
  axisLeft,
  axisRight,
  tooltip,
}) => {
  const theme = useTheme();

  const series = useMemo(
    () =>
      model.series.map((item): BarSeriesType | LineSeriesType => {
        const common = {
          id: item.id,
          label: item.label,
          data: item.data,
          yAxisId: item.axis,
          color: theme.palette[item.color].main,
          valueFormatter: (
            value: number | null,
            context: { dataIndex: number }
          ) => tooltip(value, context.dataIndex, item.id),
        };
        return item.type === "bar"
          ? { ...common, type: "bar" }
          : { ...common, type: "line", curve: "monotoneX", showMark: false };
      }),
    [model.series, theme, tooltip]
  );

  const yAxis = useMemo(
    () => [
      {
        id: "left",
        position: "left" as const,
        valueFormatter: (value: number) => axisLeft(value),
      },
      ...(axisRight
        ? [
            {
              id: "right",
              position: "right" as const,
              valueFormatter: (value: number) => axisRight(value),
            },
          ]
        : []),
    ],
    [axisLeft, axisRight]
  );

  return (
    <ChartsDataProvider
      height={height}
      series={series}
      xAxis={[{ id: "steps", scaleType: "band", data: model.labels }]}
      yAxis={yAxis}
    >
      <ChartsWrapper
        legendPosition={{ vertical: "bottom", horizontal: "center" }}
      >
        <ChartsLegend toggleVisibilityOnClick />
        <ChartsSurface>
          <ChartsGrid horizontal />
          <BarPlot borderRadius={4} />
          <LinePlot />
          <MarkPlot />
          <ChartsAxisHighlight x="band" />
          <ChartsAxis />
        </ChartsSurface>
        <ChartsTooltip trigger="axis" />
      </ChartsWrapper>
    </ChartsDataProvider>
  );
};
